import type { vtkViewProxy } from '@kitware/vtk.js/Proxy/Core/ViewProxy';
import type { MaybeRef } from 'vue';

import type { ViewProxyType } from '../core/proxies';

import { computed, ref, unref, watch } from 'vue';

import { useViewStore } from '../store/views';
import { useVTKCallback } from './useVTKCallback';

export function useViewProxy<T extends vtkViewProxy = vtkViewProxy>(
  id: MaybeRef<string>,
  type: MaybeRef<ViewProxyType>,
) {
  const viewStore = useViewStore();

  const container = ref<HTMLElement | null>(null);

  const setContainer = (el: HTMLElement | null | undefined) => {
    container.value = el ?? null;
  };

  const viewProxy = computed<T>(() =>
    viewStore.createOrGetViewProxy(unref(id), unref(type)),
  );

  watch(viewProxy, (curViewProxy, oldViewProxy) => {
    oldViewProxy.setContainer(null);
    curViewProxy.setContainer(container.value);
  });

  watch(container, (curContainer) => {
    viewProxy.value.setContainer(curContainer);
    // setContainer doesn't call modified
    viewProxy.value.modified();
  });

  return {
    viewProxy,
    setContainer,
  };
}

function onViewProxyModified<T extends vtkViewProxy = vtkViewProxy>(
  viewProxy: MaybeRef<T>,
  callback: () => void,
) {
  const onModified = useVTKCallback(
    computed(() => unref(viewProxy).onModified.bind(unref(viewProxy))),
  );
  onModified(callback);
}

function useMountedViewProxy<T extends vtkViewProxy = vtkViewProxy>(
  viewProxy: MaybeRef<T>,
) {
  const mounted = ref(false);

  const updateMounted = () => {
    mounted.value = !!unref(viewProxy).getContainer();
  };

  updateMounted();

  onViewProxyModified(viewProxy, updateMounted);

  return mounted;
}

export function useViewProxyMounted<T extends vtkViewProxy = vtkViewProxy>(
  viewProxy: MaybeRef<T>,
  callback: () => void,
) {
  const mounted = useMountedViewProxy(viewProxy);

  watch(
    mounted,
    (m) => {
      if (m) callback();
    },
    { immediate: true },
  );
}

export function useViewProxyUnmounted<T extends vtkViewProxy = vtkViewProxy>(
  viewProxy: MaybeRef<T>,
  callback: () => void,
) {
  const mounted = useMountedViewProxy(viewProxy);

  watch(
    mounted,
    (m, prev) => {
      if (prev && !m) callback();
    },
    { immediate: true },
  );
}
